import type { RefObject } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SlideOut } from '~/chords';
import type { SlideOutRef } from '~/chords/SlideOut/types';
import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { Input } from '~/components/Input';
import type { LabelMappingFlatten } from '~/modules/label-perf/api/label-mapping';
import {
  createLabelMapping,
  deleteLabelMapping,
  getUnmappedLabels,
} from '~/modules/label-perf/api/label-mapping';
import type { DropdownOption } from '~/modules/label-perf/components/Dropdown/Dropdown';
import { Dropdown } from '~/modules/label-perf/components/Dropdown/Dropdown';

export interface LabelFormProps {
  label?: LabelMappingFlatten;
  className?: string;
  onClose?: () => void;
  panelRef: RefObject<SlideOutRef>;
}

export function LabelForm({ label, onClose, panelRef }: LabelFormProps) {
  const { t } = useTranslation('label-perf');
  const [mappedOptions, setMappedOptions] = useState<DropdownOption[]>([]);
  const [existingMappings, setExistingMappings] = useState<string[]>([]);
  const [selectedMappings, setSelectedMappings] = useState<string[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (!label) {
          return;
        }

        const initialMapped =
          label.mapped_labels.map((m) => m.mapped_label) || [];

        setExistingMappings(initialMapped);
        setSelectedMappings(initialMapped);

        const unmapped = await getUnmappedLabels();
        const combined = new Set<string>([...initialMapped, ...unmapped]);
        const opts: DropdownOption[] = Array.from(combined).map((m) => ({
          label: m,
          value: m,
        }));

        setMappedOptions(opts);
      } catch (_error) {
        setError(`Failed to load label data: ${(_error as Error).message}`);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [label]);

  const willBeDeleted = useMemo(
    () => existingMappings.filter((m) => !selectedMappings.includes(m)),
    [existingMappings, selectedMappings],
  );

  if (!label) {
    return null;
  }

  const handleSave = async (): Promise<void> => {
    setError(null);

    if (selectedMappings.length === 0 && willBeDeleted.length === 0) {
      setError('Please select at least one mapped label.');

      return;
    }

    setSaving(true);

    try {
      const toAdd = selectedMappings.filter(
        (m) => !existingMappings.includes(m),
      );
      const toRemove = existingMappings.filter(
        (m) => !selectedMappings.includes(m),
      );

      await Promise.all([
        ...toAdd.map((m) => createLabelMapping(label.label_id, m, label.name)),
        ...toRemove.map(async (m) => {
          const mapping = label.mapped_labels.find(
            (ml) => ml.mapped_label === m,
          );

          if (mapping?.id) {
            await deleteLabelMapping(mapping.id);
          }
        }),
      ]);

      onClose?.();
    } catch (_error) {
      setError(`Failed to save changes: ${(_error as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlideOut panelWidth="560px" ref={panelRef}>
      <SlideOut.Header>
        {t('body.editLabelMapping', 'Edit Mapping')}
      </SlideOut.Header>
      <SlideOut.Body>
        {loading ? (
          <div className="my-md" data-testid="initial-loading">
            <Icon
              type="spinner"
              variant="solid"
              size="sm"
              className="fa-spin my-md"
            />
          </div>
        ) : (
          <>
            {label.parent_path ? (
              <div className="my-md">
                <p className="mb-sm text-body-sm font-semibold text-default">
                  {t('body.parentPath', 'Parent Path')}
                </p>
                <Input.Text
                  onChange={() => {}}
                  readOnly
                  value={label.parent_path}
                />
              </div>
            ) : null}

            <div className="mb-md">
              <p className="mb-sm text-body-sm font-semibold text-default">
                {t('body.labelName', 'Label Name')}
              </p>
              <Input.Text onChange={() => {}} readOnly value={label.name} />
            </div>

            <div className="mb-md">
              <p className="mb-sm text-body-sm font-semibold text-default">
                {t('body.mappedLabels', 'Mapped Label(s)')}
              </p>
              <Dropdown
                isMulti
                onChange={(options) => {
                  if (Array.isArray(options)) {
                    setSelectedMappings(options.map((opt) => opt.value));
                  }
                }}
                options={mappedOptions}
                value={selectedMappings}
              />
            </div>

            {willBeDeleted.length > 0 ? (
              <div className="mb-md" data-testid="deletion-section">
                <p className="mb-sm text-body-sm font-semibold text-warning-default">
                  {t('body.willBeDeleted', 'Will be deleted on save')}
                </p>
                <ul className="ml-md list-disc space-y-xs text-body-sm text-warning-default">
                  {willBeDeleted.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}

        {error ? (
          <div className="mb-sm text-body-sm text-error-default">
            <p>{error}</p>
          </div>
        ) : null}
      </SlideOut.Body>
      <SlideOut.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={onClose}
          disabled={saving}
        >
          {t('actions.cancel', 'Cancel')}
        </Button>
        <Button
          size="large"
          variant="primary"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving
            ? t('actions.saving', 'Saving...')
            : t('actions.save', 'Save')}
        </Button>
      </SlideOut.Footer>
    </SlideOut>
  );
}

LabelForm.displayName = 'LabelForm';
